import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
  untracked
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { AuthService } from '@coin/shared/auth/data-access';
import { V2TurbineComponent } from '@coin/shared/core/ui-storybook';
import { FaqDrill, FaqItem } from '@coin/shared/core/util-models';
import { TranslateModule } from '@ngx-translate/core';
import { from } from 'rxjs';
import { FaqAnswerLevelComponent } from '../faq-answer-level/faq-answer-level.component';
import { FaqInbetweenLevelComponent } from '../faq-inbetween-level/faq-inbetween-level.component';
import { FaqTopLevelComponent } from '../faq-top-level/faq-top-level.component';

@Component({
  selector: 'coin-chat-faq-menu',
  templateUrl: './chat-faq-menu.component.html',
  styleUrl: './chat-faq-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIcon,
    MatTab,
    MatTabGroup,
    TranslateModule,
    V2TurbineComponent,
    FaqAnswerLevelComponent,
    FaqInbetweenLevelComponent,
    FaqTopLevelComponent
  ]
})
export class ChatFaqMenuComponent {
  private authService = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private activeUser = toSignal(from(this.authService.getActiveUser()));

  readonly helpData = input<FaqItem[]>([]);
  readonly isLoading = input(false);

  // eslint-disable-next-line @angular-eslint/no-output-native
  readonly close = output();
  readonly openOtherTopic = output<string>();

  constructor() {
    effect(() => {
      this.helpData();
      untracked(() => this.reduceOnePage(true));
    });
  }

  public faqTabs = signal<FaqItem[]>([]);
  public search = signal('');
  public faqTabIndex = signal(0);
  public generalTabIndex = signal(0);
  public topicTitle = signal('help');
  public subtitle = signal<string | boolean | undefined>(undefined);
  public faqMoreWidth = signal(false);
  private searchActive = false;

  public searchFaq(): void {
    const searchItem = this.search().trim().toLowerCase();
    this.faqTabs.set([]);
    if (this.search() && this.search() !== '') {
      const allAnswers = this.getAllFaqAnswers();
      const finalSearchResult: FaqItem[] = [];
      for (const item of allAnswers) {
        if (
          JSON.stringify(item.content).toLowerCase().indexOf(searchItem) > -1 ||
          JSON.stringify(item.title).toLowerCase().indexOf(searchItem) > -1
        ) {
          finalSearchResult.push(item);
        }
      }
      this.searchActive = true;
      this.openFaqItem({ content: finalSearchResult } as FaqItem, 2, false, 'search-result', false);
    } else {
      this.reduceOnePage(true);
    }
  }

  public closeFaq(): void {
    this.close.emit();
  }

  public openFaqFromSub(data: FaqDrill): void {
    this.openFaqItem(data.content, data.level, data.moreWidth, data.title, data.subtitle);
  }

  public onOpenOtherTopic(data: { title: string }): void {
    this.openOtherTopic.emit(data.title);
  }

  private openFaqItem(
    faqItem: FaqItem,
    level: number,
    moreWidth = false,
    title?: string | boolean,
    subtitle?: string | boolean
  ): void {
    if (title || (level === 1 && faqItem.title)) {
      this.topicTitle.set(faqItem.title);
    }
    if (!faqItem.isAnswer) {
      this.subtitle.set(subtitle);
    }
    this.faqMoreWidth.set(moreWidth);
    if (level > 0) {
      const user = this.activeUser();
      const country = user?.['custom:country'] || user?.country;

      faqItem.content = faqItem.content.filter(itm => {
        if (itm.requirement?.type !== 'isROW' || !country) return true;
        const isDE = country === 'DE';
        return isDE ? !itm.requirement.value : itm.requirement.value !== false;
      });

      this.faqTabs.update(tabs => [...tabs, faqItem]);
      this.cdr.detectChanges();

      // Defer index switch to next repaint so mat-tab-group can animate the transition
      requestAnimationFrame(() => {
        this.faqTabIndex.update(index => index + 1);
        this.cdr.markForCheck();
      });
    }
  }

  public reduceOnePage(clear?: boolean): void {
    let newSubtitle: string | boolean | undefined = false;
    if ((this.searchActive && this.faqTabIndex() === 1) || clear) {
      this.faqTabIndex.set(0);
      this.search.set('');
      this.topicTitle.set('help');
      this.faqTabs.set([]);
    } else if (this.faqTabIndex() === 2) {
      newSubtitle = this.subtitle();
    }
    this.subtitle.set(newSubtitle);
    this.faqMoreWidth.set(false);
    if (this.faqTabIndex() > 0) {
      this.faqTabIndex.update(index => index - 1);
      this.faqTabs.update(tabs => {
        tabs.pop();
        return [...tabs];
      });
      if (this.faqTabIndex() === 0) {
        this.topicTitle.set('help');
        this.search.set('');
      }
    } else if (this.generalTabIndex() > 0) {
      this.generalTabIndex.set(0);
      this.faqTabs.set([]);
    }
  }

  private getAllFaqAnswers(): FaqItem[] {
    const finalList: FaqItem[] = [];
    for (const topItem of this.helpData()) {
      this.iterateToBottom(topItem, finalList);
    }
    return finalList;
  }

  private iterateToBottom(list: FaqItem, finalList: FaqItem[]): void {
    if (list && list.isAnswer) {
      finalList.push(list);
    } else if (list) {
      for (const item of list.content) {
        this.iterateToBottom(item, finalList);
      }
    }
  }
}
